import apiClient from '@/shared/services/apiClient';

/**
 * Patient-facing BioMatch enrollment client.
 *
 * The BE returns one of two shapes depending on the cost split:
 *
 *   201 BioMatchEnrollment            — patient owed $0; row created.
 *   202 EnrollmentCheckoutIntent      — patient owes >$0; FE redirects
 *                                       to checkout to complete payment.
 *
 * The apiClient's default behavior parses both 2xx responses as JSON;
 * we discriminate via the `requires_payment` field.
 *
 * 409 (already enrolled) flows through apiClient's error path with
 * `status === 409`.
 */

export type EnrollmentStatus = 'active' | 'cancelled' | 'completed' | 'paused';
export type FulfillmentMethod = 'home_kit' | 'add_to_existing_draw';

export interface BioMatchEnrollment {
    id: number;
    user_id: number;
    recommendation_id: number;
    organization_id: number | null;
    kit_sku: string | null;
    recommendation_name: string;
    status: EnrollmentStatus;
    source: string;
    enrolled_at: string;
    next_test_due: string | null;
    cancelled_at: string | null;
    cancelled_reason: string | null;
    billing_type: string;
    employer_coverage_percent_snapshot: number | null;
    kit_price_cents: number;
    patient_amount_cents: number;
    employer_amount_cents: number;
    fulfillment_method: FulfillmentMethod;
    fulfillment_status: string;
    storefront_order_id: string | null;
}

export interface EnrollmentCheckoutIntent {
    requires_payment: true;
    recommendation_id: number;
    kit_sku: string | null;
    kit_price_cents: number;
    patient_amount_cents: number;
    employer_amount_cents: number;
    fulfillment_method: FulfillmentMethod;
}

export type EnrollResult = BioMatchEnrollment | EnrollmentCheckoutIntent;

export function isCheckoutIntent(result: EnrollResult): result is EnrollmentCheckoutIntent {
    return (result as EnrollmentCheckoutIntent).requires_payment === true;
}

export interface CheckoutTicket {
    provider: 'fortis' | 'stripe';
    clientToken: string;
    publishableKey?: string;
}

export interface CheckoutFinalizeResult {
    enrollmentId: number;
    storefrontOrderId: string;
    paymentStatus: 'CAPTURED' | 'PENDING' | 'FAILED';
    patientAmountCents: number;
    employerAmountCents: number;
}

/** BioMatch v2 — assembled patient hero. Fully hydrated; FE just renders. */
export interface CustomLabPackageAssay {
    code: string;
    name: string;
    /** LOINC code for severity matching (name fallback). Always present in the payload. */
    loinc: string | null;
    category: number | null;
    is_calculated: boolean;
    payg_price_cents: number;
    standard_price_cents: number;
    serum_ia_ul: number | null;
    serum_chem_ul: number | null;
    sourced_from: number[];
    bundled_panel_code: string | null;
}

export interface KitCostBreakdown {
    one_time_total_cents: number;
    tests_cents: number;
    devices_cents: number;
    package_cents: number;
    shipping_cents: number;
    handling_cents: number;
    total_devices: number;
    devices_for_serum: number;
    spare_device_included: boolean;
    total_serum_ul: number;
    serum_ia_ul: number;
    serum_chem_ul: number;
}

export interface CustomLabPackageProduct {
    id: number;
    name: string;
    /** Storefront marketing copy shown under the product name on the
     *  BioMatch card. Optional for compatibility with API nodes deployed
     *  before this field; empty string when the product row has none. */
    description?: string;
    image_url: string | null;
    price_cents: number;
    member_price_cents: number | null;
    category: string;
    /** Subscription cadence (days). NULL = one-time only. */
    default_cadence_days: number | null;
    cadence_label: string | null;
    supply_per_shipment: string | null;
    /** Per-product monthly contribution to membership = price / (cadence/30). */
    member_monthly_cents: number;
    /** Whether this row's price/member_price came from a per-org override. */
    price_overridden: boolean;
    sourced_from: number[];
}

export interface BundledPanelGroup {
    code: string;
    name: string;
    payg_price_cents: number;
    standard_price_cents: number;
    component_assay_codes: string[];
    /** Result markers produced by the panel. Optional for rolling-deploy
     * compatibility; these are display candidates, never checkout units. */
    included_markers?: Array<{code: string; name: string; loinc: string | null}>;
}

/** A distinct biomarker rule that matched the patient's result. These drive
 * the card's marker count and chips; `assays` remain the checkout units. */
export interface MatchedMarker {
    code: string;
    name: string;
    loinc: string | null;
    sourced_from: number[];
    bundled_panel_code: string | null;
}

export interface CustomLabPackage {
    patient_first_name: string;
    assays: CustomLabPackageAssay[];
    /** Optional for compatibility with API nodes deployed before this field.
     * An explicitly empty array means no lab marker matched. */
    matched_markers?: MatchedMarker[];
    products: CustomLabPackageProduct[];
    /** Assays bundled by the recommended products — display-only (checkout
     * and enrollment ignore these; ELIS resolves them from the SKU).
     * Optional for compatibility with API nodes deployed before this field. */
    product_included_assays?: Array<{code: string; name: string; loinc: string | null}>;
    bundled_panels: BundledPanelGroup[];
    pricing: {
        one_time_cents: number;
        member_monthly_cents: number;
        kit_breakdown: KitCostBreakdown;
    };
}

export interface CuratedRecProduct {
    id: number;
    name: string;
    slug: string;
    description: string;
    category: string;
    image_url: string | null;
    permalink: string | null;
    price_cents: number;
    member_price_cents: number | null;
    currency: string;
    is_featured: boolean;
}

/**
 * Curated rec card. `cart_products` is server-hydrated from the rec's
 * recommended_products and is used by the supplement-type CTA to push
 * a real storefront product into the cart (the BioMatch enrollment
 * endpoint only accepts program-type recs).
 */
export interface CuratedCard {
    id: number;
    name: string;
    image: string | null;
    price: number | null;
    members_price: number | null;
    url: string | null;
    pathway_url: string | null;
    recommendation_type: 'supplement' | 'program' | 'care_pathway' | 'referral' | 'lifestyle';
    cta_button_label: string | null;
    cta_body_text: string | null;
    billing_type: 'employer_full' | 'employer_partial' | 'patient_full';
    employer_coverage_percent: number | null;
    cart_products: CuratedRecProduct[];
    /**
     * The lab/biometric rules that matched to trigger this recommendation.
     * `test_type` is the display label (e.g. "Estradiol") and is what the
     * "Based On Your … Value" pill shows so the patient sees which lab value
     * the product targets. `rule_source` distinguishes a true lab biomarker
     * from a biometric (bmi/waist) or age rule.
     */
    biomarkers?: Array<{
        test_type: string;
        rule_source?: 'biomarker' | 'biometric' | 'age' | string;
        assay_code?: string | null;
    }> | null;
}

export interface AssembledBioMatchResponse {
    /** Each matched curated rec → one card on the dashboard hero. */
    curated_cards: CuratedCard[];
    custom_package: CustomLabPackage | null;
    /** Org member discount percent (0-100); null = none. */
    mylabs_plus_member_discount_pct: number | null;
}

export const BioMatchEnrollmentService = {
    async enroll(recommendationId: number): Promise<EnrollResult> {
        return apiClient.post<EnrollResult>('/api/biomatch/enrollments', {
            recommendation_id: recommendationId,
        });
    },

    /**
     * Patient hero data — curated cards + the deduped Custom Lab
     * Package built from matched range recs. Server has already
     * applied the auto-subtract and pulled in calc_prerequisites.
     */
    async getMyAssembled(): Promise<AssembledBioMatchResponse> {
        return apiClient.get<AssembledBioMatchResponse>('/api/biomatch/dashboard/mine');
    },

    /**
     * Custom Lab Package checkout — Fortis ticket intention. The FE
     * widget mounts using the returned `clientToken`.
     */
    async createCustomKitTicket(): Promise<CheckoutTicket> {
        return apiClient.post<CheckoutTicket>('/api/biomatch/custom-kit/checkout/ticket', {});
    },

    /**
     * Custom Lab Package checkout — finalize. Server re-runs the
     * assembly, validates the amount, charges Fortis, snapshots the
     * kit composition into biomatch_enrollment_assays/products.
     */
    async finalizeCustomKitCheckout(input: {
        checkout_attempt_id: string;
        mode: 'one_time' | 'member';
        ticket_id: string;
        save_card?: boolean;
        client_expected_amount_cents?: number;
    }): Promise<{
        enrollmentId: number;
        storefrontOrderId: string;
        paymentStatus: 'CAPTURED' | 'PENDING' | 'FAILED';
        chargedAmountCents: number;
        mode: 'one_time' | 'member';
        nextTestDue: string | null;
    }> {
        return apiClient.post('/api/biomatch/custom-kit/checkout', input);
    },

    async listMine(includeAll = false): Promise<BioMatchEnrollment[]> {
        const qs = includeAll ? '?include=all' : '';
        return apiClient.get<BioMatchEnrollment[]>(`/api/biomatch/enrollments/mine${qs}`);
    },

    async subscriptionAction(
        id: number,
        action: 'cancel' | 'pause' | 'resume' | 'reschedule',
        body: {reason?: string; next_test_due?: string}
    ): Promise<{status: string; next_test_due?: string}> {
        return apiClient.post(`/api/biomatch/subscriptions/${id}/${action}`, body);
    },

    /**
     * Patient-pays checkout (4c) — creates a Fortis ticket intention.
     * The FE Elements widget mounts using `clientToken` and tokenizes
     * the card.
     */
    async createCheckoutTicket(): Promise<CheckoutTicket> {
        return apiClient.post<CheckoutTicket>('/api/biomatch/enrollments/checkout/ticket', {});
    },

    /**
     * Patient-pays checkout (4c) — finalize the enrollment after the
     * patient tokenizes their card. The server re-validates the
     * amount and creates payment_transaction + storefront_order +
     * biomatch_enrollment in one transaction.
     */
    async finalizeCheckout(
        recommendationId: number,
        ticketId: string,
        checkoutAttemptId: string,
        saveCard = false,
        shippingAddress?: {line1: string; line2?: string | null; city: string; state: string; zip: string}
    ): Promise<CheckoutFinalizeResult> {
        return apiClient.post<CheckoutFinalizeResult>('/api/biomatch/enrollments/checkout', {
            recommendation_id: recommendationId,
            ticket_id: ticketId,
            checkout_attempt_id: checkoutAttemptId,
            save_card: saveCard,
            shipping_address: shippingAddress,
        });
    },

    /**
     * Fire-and-forget impression recorder.
     *
     * Two layers of dedup:
     *   1. Client (this function): sessionStorage keyed by
     *      (recId, kind, UTC-date) suppresses React Strict Mode
     *      double-invokes, React Query refetches, and same-tab
     *      remounts. Cuts a typical page render from ~15 POSTs down
     *      to one per unique (rec, kind).
     *   2. Server (dataAccess.recordImpression): unique-per-UTC-day
     *      per (user, rec, kind) — authoritative source for the
     *      biomatch_impressions table.
     *
     * If sessionStorage isn't available (SSR, sandboxed iframe,
     * privacy mode), we fall through to the network — the server
     * dedup keeps the table clean either way.
     */
    async recordImpression(recommendationId: number, impressionKind: 'view' | 'click' | 'dismiss'): Promise<void> {
        if (clientImpressionRecorded(recommendationId, impressionKind)) {
            return;
        }
        try {
            await apiClient.post<{recorded: boolean}>('/api/biomatch/impressions', {
                recommendation_id: recommendationId,
                impression_kind: impressionKind,
            });
        } catch {
            // Non-blocking — analytics should never break the UI.
        }
    },
};

function clientImpressionRecorded(recommendationId: number, impressionKind: 'view' | 'click' | 'dismiss'): boolean {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return false;
        const utcDate = new Date().toISOString().slice(0, 10);
        const key = `biomatch_impression_${recommendationId}_${impressionKind}_${utcDate}`;
        if (window.sessionStorage.getItem(key)) return true;
        window.sessionStorage.setItem(key, '1');
        return false;
    } catch {
        // Quota exceeded / disabled storage / etc. — let the network
        // call go through; server dedup catches the duplicate.
        return false;
    }
}

export default BioMatchEnrollmentService;
